import { faExpand } from '@fortawesome/pro-solid-svg-icons/faExpand';
import { ResizeObserver } from '@juggle/resize-observer';
import { mobileXXLargeMax } from '@propertypal/shared/src/constants/mediaQueries';
import { IMedia } from '@propertypal/shared/src/types/strapi';
import React, { useState } from 'react';
import useMeasure from 'react-use-measure';
import { Box } from '../layout';
import useMediaQuery from '../media-query/useMediaQuery';
import ModalWrapper from '../modal/ModalWrapper';
import { ExpandIcon, GalleryImg, GalleryImgWrapper, ImageGrid, ImageRow } from './ArticleGallery.style';
import GalleryCarousel from './GalleryCarousel';

export const getStrapiGalleryChunks = (images: IMedia[], chunksizes: number[]) => {
  const chunkArray: { keyItem: number; images: IMedia[] }[] = [];
  let cc = 0;
  let i = 0;
  let j = 0;
  while (i < images.length) {
    const csize = chunksizes[cc];
    chunkArray.push({ keyItem: (j += 1), images: images.slice(i, i + csize) });
    cc = (cc + 1) % chunksizes.length;
    i += csize;
  }

  // Combine orphan images with the row above
  if (
    chunksizes[0] > 1 &&
    chunkArray.length > 1 &&
    chunkArray[chunkArray.length - 1].images.length === 1 &&
    chunkArray[chunkArray.length - 2].images.length === 2
  ) {
    const row = chunkArray.pop();
    if (row) chunkArray[chunkArray.length - 1].images.push(row.images[0]);
  }

  return chunkArray;
};

export const getStrapiTallestHeight = (arrImages: IMedia[]) => {
  let tallestHeight = 0;
  for (let i = 0; i < arrImages.length; i += 1) {
    const current = arrImages[i];
    const oheight = current.attributes.height || 0;
    if (oheight > tallestHeight) tallestHeight = oheight;
  }

  return tallestHeight;
};

export const getStrapiImageChunkWidths = (arrImages: IMedia[], windowWidth: number) => {
  // const baselinePercentage = 100 / lengthOfArray;
  const screenWidth = windowWidth;
  const yMax = getStrapiTallestHeight(arrImages);

  // Calculate target image widths
  let imageWidthTotal = 0;
  const imageWidths: any[] = [];

  arrImages.forEach((img) => {
    const width = img.attributes.width || 0;
    const height = img.attributes.height || 0;
    let ratio = 1;
    let w = 0;
    let h = 0;

    if (height && width) {
      ratio = width / height;
      w = width * (yMax / height);
      h = w / ratio;
    }

    imageWidthTotal += w;
    imageWidths.push({ width: w, height: h, ratio });
  });

  // Convert widths to percentages
  for (let i = 0; i < imageWidths.length; i += 1) {
    const decimalPercentValue = imageWidthTotal ? imageWidths[i].width / imageWidthTotal : 0;
    const ratio = imageWidths[i].ratio;

    // 375 *  0.45 * / 1.5
    const calculatedHeight = (screenWidth * decimalPercentValue) / ratio;

    imageWidths[i] = {
      width: `${decimalPercentValue * 100}%`,
      height: Math.round(calculatedHeight),
    };
  }
  return imageWidths;
};

interface ArticleGalleryProps {
  gallery: IMedia[];
  alt: string;
}

const ArticleGallery: React.FC<ArticleGalleryProps> = ({ gallery, alt }) => {
  const [imageIndex, setImageIndex] = useState<number>(0);
  const [galleryOpen, setGalleryOpen] = useState<boolean>(false);
  const isMobile = useMediaQuery({ query: `(${mobileXXLargeMax})` });
  const chunks = getStrapiGalleryChunks(gallery, isMobile ? [1, 2] : [2, 3]);
  // use to get width of container to use to determine chunk widths so images are too large
  const [ref, { width }] = useMeasure({ polyfill: ResizeObserver });

  return (
    <Box data-testid="article-gallery" mt={40} ref={ref}>
      <ModalWrapper show={galleryOpen} onClose={() => setGalleryOpen(false)}>
        <GalleryCarousel
          imageIndex={imageIndex}
          images={gallery.map((img, i) => ({ ...img.attributes, index: i }))}
          setImageIndex={setImageIndex}
          onClose={() => setGalleryOpen(false)}
          alt={alt}
        />
      </ModalWrapper>
      <ImageGrid id="image-grid">
        {chunks.map((chunk, idx) => {
          const imageWidths = getStrapiImageChunkWidths(chunk.images, width || 0);

          const previousItems = chunks?.slice(0, idx)?.reduce((a, s) => {
            a += s.images.length;
            return a;
          }, 0);

          return (
            <ImageRow key={chunk.keyItem}>
              {chunk.images.map((img, i) => (
                <GalleryImgWrapper
                  key={img.id}
                  total={gallery.length}
                  onClick={() => {
                    setGalleryOpen(true);
                    setImageIndex(i + previousItems);
                  }}
                  data-testid="article-gallery-img"
                  style={{ ...imageWidths[i] }}
                  title={img.attributes.caption || img.attributes.alternativeText || undefined}
                >
                  <GalleryImg
                    src={img.attributes.formats?.medium?.url || img.attributes.url}
                    alt={img.attributes.alternativeText || img.attributes.caption || alt}
                  />
                  <ExpandIcon icon={faExpand} size={30} />
                </GalleryImgWrapper>
              ))}
            </ImageRow>
          );
        })}
      </ImageGrid>
    </Box>
  );
};

export default ArticleGallery;
